// @ts-strict-ignore
import { ProjectInfoPropertyDef } from '@project/components/project/constants/project-info-property-def';
import { Log } from '@shared/debug/log';
import { Dictionary } from 'typescript-collections';
import { ArrayUtils } from '@shared/util/array-utils';
import { StringUtils } from '@shared/util/string-utils';
import { IsProjectType } from './is-project-type';
import { ArticleInfoVO, ProjectHeadVO, ProjectVO } from '@project/model/vo/project.vo';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { RepresentationVO } from '@shared/vo/representation.vo';
import { NumberUtils } from '@shared/util/number-utils';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { ProjectOptionUtils } from '../components/project/util/project-option.utils';
import { ProjectGroupUtils } from '../components/project/util/project-group-utils';
import { ProjectGroupVO } from './vo/project-group.vo';
import { ProjectChanged, ProjectChangedType } from './project-changed';
import { Injectable, OnDestroy } from '@angular/core';
import { UserModel } from '@user/model/user.model';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { UserOptionDefsVO } from '@user/model/vo/user-option-defs.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ProjectType } from '@project/model/project-type';
import { Properties } from '@article/util/properties';

@Injectable({ providedIn: 'root' })
export class ProjectModel implements OnDestroy {
  private readonly _projectChanged$ = new Subject<ProjectChanged>();
  private readonly _currentProjectType$ = new BehaviorSubject<ProjectType | undefined>(undefined);

  public texturesEnabled: boolean = true;
  public ignoreSplitConnectionCubes: boolean = false;

  public lastBlockingZoneLength: number;
  public lastBlockingZoneOffset: number;
  public lastDrillingHoleLength: number;
  public lastDrillingHoleOffset: number;

  public isAdminView: boolean = false;
  public isCompositeAdminView: boolean = false;
  public compositeAdminBaseArticleUsage: ArticleUsageVO;

  private _currentProject$: BehaviorSubject<ProjectVO> = new BehaviorSubject(null);

  public corruptArticles: ArticleContainer[] = [];

  public get currentProject$(): Observable<ProjectVO> {
    return this._currentProject$.asObservable();
  }


  private _projectMetaDataChanged: boolean = false;
  private _hasProjectChanges: boolean = false;

  private _articleUsageCache: Dictionary<string, ArticleUsageVO> = new Dictionary<string, ArticleUsageVO>();

  public latestProjectVersionNumber: number = -1;

  private _isMountingRailInProcess: boolean = false;
  public projectMountingRailIsNew: boolean = false;

  public view3dRepresentationForSavePurposes: RepresentationVO;
  public view3dRepresentationForCadExportPurposes: RepresentationVO;
  public view3dRepresentationForValidationPurposes: RepresentationVO;

  public articleInsertedSubscription: Subscription;
  private _onWireBridgeCountersReset$ = new Subject<void>();

  // Base article (only MCS/GDS projects).
  private _baseArticle: ArticleVO | undefined;

  constructor(
    public userModel: UserModel,
  ) {  }

  public get projectChanged$(): Observable<ProjectChanged> {
    return this._projectChanged$.asObservable();
  }

  public get articleUsageCache () {
    return this._articleUsageCache;
  }

  public set articleUsageCache (articleUsageCache: Dictionary<string, ArticleUsageVO>) {
    this._articleUsageCache = articleUsageCache;
  }

  public get onWireBridgeCountersReset$(): Observable<void> {
    return this._onWireBridgeCountersReset$.asObservable();
  }

  public get currentProject(): ProjectVO {
    return this._currentProject$.getValue();
  }

  public set currentProject(project: ProjectVO) {
    this.corruptArticles = [];
    this._onWireBridgeCountersReset$.next(undefined);
    this.setArticleUsagesCaches(project);
    this._currentProject$.next(project);
    this.resetProjectChanged();
    this.projectTypeChanged();
  }

  public get configurationGroup(): ProjectGroupVO {
    return this.currentProject ? this.currentProject.projectGroup : null;
  }

  public get isNewConfiguration(): boolean {
    if (!this.currentProject) {
      return true;
    }
    return ProjectUtils.isNewProject(this.currentProject);
  }


  public get isNewProject(): boolean {
    if (this.isNewConfigurationGroup && !ProjectUtils.isNewWSPImportedProject(this.currentProject)) {
      return true;
    }
    return ProjectUtils.isNewProject(this.currentProject);
  }


  public get isNewConfigurationGroup(): boolean {
    return ProjectGroupUtils.isNewConfigurationGroup(this.currentProject?.projectGroup);
  }

  public get isProjectOpenedByTemporaryLink(): boolean {
    if (!this.currentProject) {
      return false;
    }
    return StringUtils.isNotEmpty(this.currentProject.temporaryLinkGUID);
  }

  public get currentProjectType$(): Observable<ProjectType> {
    return this._currentProjectType$.asObservable();
  }

  public get currentProjectType(): ProjectType | undefined {
    return this.currentProject?.projectType;
  }

  public set currentProjectType(projectType: ProjectType) {
    if (!this.currentProject) {
      return;
    }
    if (this.currentProject.projectType !== projectType) {
      this.currentProject.projectType = projectType;
      this.projectTypeChanged();
    }
  }


  public get currentInfoProperties(): Properties<ProjectInfoPropertyDef> {
    if(Log.assertExists(this.currentProject)) {
      return this.currentProject.infoProperties;
    }
    return new Properties<ProjectInfoPropertyDef>([]);
  }

  public get baseArticle(): ArticleVO | undefined {
    return this._baseArticle;
  }

  public set baseArticle(article: ArticleVO | undefined) {
    if(this._baseArticle == article) return;
    this._baseArticle = article;
  }

  public ngOnDestroy(): void {
    this.articleInsertedSubscription.unsubscribe();
    this.articleInsertedSubscription = undefined;
  }

  public resetAdminProjectChanged(): void {
    if (this.currentProject && IsProjectType.admin(this.currentProject.projectType)) {
      this.resetProjectChanged();
    }
  }

  public get isCurrentProjectArticleProject(): boolean {
    return this.currentProject.isArticleProject;
  }

  public get isCurrentProjectChanged(): boolean {
    return this._hasProjectChanges || this._projectMetaDataChanged;
  }

  public get isCurrentProjectConfigurationGroup(): boolean {
    if (!this.currentProject || !ProjectGroupUtils.isConfigurationGroup(this.currentProject.projectGroup)) {
      return false;
    }
    return true;
  }

  public get currentProjectRequiresSaving(): boolean {
    return (
      this.isCurrentProjectChanged ||
      (this.isNewConfiguration &&
        !ProjectUtils.isEmptyProject(this.currentProject) &&
        this.currentProject &&
        !IsProjectType.admin(this.currentProject.projectType))
    );
  }

  public set projectDataChanged(value: boolean) {
    this._hasProjectChanges = value;
    this.notifyCurrentProjectChangedInternal();
  }

  public set projectMetaDataChanged(value: boolean) {
    this._projectMetaDataChanged = value;
    this.notifyCurrentProjectChangedInternal();
  }

  public resetProjectChanged(): void {
    this._projectMetaDataChanged = false;
    this._hasProjectChanges = false;
    // Dispatch event to notify binded components
    this.notifyCurrentProjectChangedInternal();
  }

  public projectChangeHandler(): void {
    this.reset3DRepresentations();
  }

  public isArticleRepresentationsEnabled(project: ProjectVO = null): boolean {
    if (!project) {
      project = this.currentProject;
    }
    return project && project.projectType === "MARKINGSYSTEM";
  }

  public applyUserOptions(options: UserOptionDefsVO): void {
    // SMAR-68:
    // Apply default project options directly to current project in case it is an new unnamed project, which was not wsp imported.
    // This means common default options are used in case no user is logged in.
    // If user is already logged in or in case he logs in later, his default project options get applied.
    // Existing values (coming from previously applied common default values) are wiped/overwritten.
    if (this.currentProject && StringUtils.isEmpty(this.currentProject.name) && !this.currentProject.isWSPImportedProject) {
      this.currentProject.options = []; // currently wiping existing project options
      ProjectOptionUtils.setDefaultProjectOptionValues(this.currentProject, options.projectOptionDefs, true);
    }
    // in any case, when current project does not yet have a project version
    // (for example old wsp imported project without such project option while no user logged in) we apply initial default value:
    if (StringUtils.isEmpty(ProjectUtils.getVersionDisplayString(this.currentProject, false))) {
      ProjectUtils.setInitialVersionDisplayString(this.currentProject, options.projectOptionDefs);
    }
  }

  public setArticleUsagesCaches(project: ProjectVO): void {
    const articleUsages = ProjectUtils.articleUsages(project)
    articleUsages.forEach((articleUsage: ArticleUsageVO) => this.articleUsageCache.setValue(articleUsage.id, articleUsage));
  }

  public getArticleUsage(guid: string): ArticleUsageVO {
    return this.articleUsageCache.getValue(guid);
  }

  public getAllArticleUsages(): ArticleUsageVO[] {
    return this.articleUsageCache.values();
  }

  public getArticleUsages(guids: string[]): ArticleUsageVO[] {
    const result: ArticleUsageVO[] = [];
    for (const usageId of guids) {
      result.push(this.getArticleUsage(usageId));
    }
    return result;
  }

  public removeArticleInfoFromCurrentProject(guid: string): void {
    const info: ArticleInfoVO = ArticleUtils.findArticleInfoByGuid(guid, this.currentProject);
    if (info) {
      const infoIndex: number = this.currentProject.dynamicArticlesInfo.indexOf(info);
      this.currentProject.dynamicArticlesInfo.splice(infoIndex, 1);
    }
  }

  public notifyCurrentProjectChanged(): void {
    this._hasProjectChanges = true;
    this.reset3DRepresentations();
    this.notifyCurrentProjectChangedInternal();
  }

  public reset3DRepresentations(): void {
    this.view3dRepresentationForSavePurposes = null;
    this.view3dRepresentationForCadExportPurposes = null;
    this.view3dRepresentationForValidationPurposes = null;
  }

  public updateArticleInfoInCurrentProject(articleInfos: ArticleInfoVO[]): void {
    if (!articleInfos || articleInfos.length === 0) {
      return;
    }
    ArrayUtils.mergeArrays(this.currentProject.dynamicArticlesInfo, articleInfos);
  }

  public addArticleInfoToCurrentProject(articleInfo: ArticleInfoVO): void {
    if (!articleInfo) {
      return;
    }

    this.updateArticleInfoInCurrentProject([articleInfo]);
  }

  public mountingRailWizardOpened(): void {
    this.isMountingRailInProcess = true;
  }

  public mountingRailWizardClosed(): void {
    this.isMountingRailInProcess = false;
  }

  public newProjectMountingRail(): void {
    this.projectMountingRailIsNew = true;
  }

  public existingProjectMountingRail(): void {
    this.projectMountingRailIsNew = false;
  }

  private projectTypeChanged(): void {
    this._currentProjectType$.next(this.currentProject?.projectType);
    this._projectChanged$.next(new ProjectChanged(this.currentProject, ProjectChangedType.ProjectTypeChanged))
  }

  public get isMountingRailInProcess(): boolean {
    return this._isMountingRailInProcess;
  }

  public set isMountingRailInProcess(value: boolean) {
    this._isMountingRailInProcess = value;
  }

  public updateArticleUsageCacheKey(guidFrom: string, guidTo: string): void {
    const articleUsage: ArticleUsageVO = this.articleUsageCache.getValue(guidFrom);
    if (articleUsage) {
      this.articleUsageCache.setValue(guidTo, articleUsage);
      this.articleUsageCache.remove(guidFrom);
    }
  }

  public getCurrentLoadedArticleUsage(): ArticleUsageVO {
    const articleUsages: ArticleUsageVO[] = ProjectUtils.articleUsages(this.currentProject);
    let articleUsage: ArticleUsageVO = null;
    if (this.isCompositeAdminView) {
      articleUsage = this.compositeAdminBaseArticleUsage;
    } else if (articleUsages && articleUsages.length > 0) {
      articleUsage = articleUsages[0];
    }
    return articleUsage;
  }

  public deleteConfiguration(projectHeadVO: ProjectHeadVO) {
    if(this.currentProject?.projectGroup) {
      // The projects within the project group might not be loaded so use the passed object for remove and event.
      // The deleted configuration might also not be part of the current project or project group.
    if (this.currentProject?.projectGroup?.projects) {
      ArrayUtils.removeFromArray(this.currentProject.projectGroup.projects, project => project.id === projectHeadVO.id);
    }
      this._projectChanged$.next(new ProjectChanged(projectHeadVO, ProjectChangedType.ConfigurationDeleted));
    }
  }

  public rename(projectVO: ProjectHeadVO, name: string) {
    projectVO.name = name;
    if (this.currentProject.id === projectVO.id) {
      this.currentProject.name = name;
    }
    const configuration = this.currentProject.projectGroup.projects.find(project => NumberUtils.equals(project.id, projectVO.id));
    if (configuration) {
      configuration.name = name;
    }
    this._projectChanged$.next(new ProjectChanged(projectVO, ProjectChangedType.ProjectRenamed));
  }

  private notifyCurrentProjectChangedInternal() {
    this._projectChanged$.next(new ProjectChanged(this.currentProject, ProjectChangedType.CurrentProjectChanged));
  }

  public sendProjectChanged(type: ProjectChangedType, project = this.currentProject, articleUsageIdsToRefresh?: string[]) {
    this._projectChanged$.next(new ProjectChanged(project, type, articleUsageIdsToRefresh || null))
  }

  /**
   * @deprecated Should not be used. Use UserModel.user instead
   */
  public get user () {
      return this.userModel.user;
  }

  public updateProjectData(project: ProjectVO) {
    this.currentProject.id = project.id;
    this.currentProject.guid = project.guid;
    this.currentProject.creationDate = project.creationDate;
    this.currentProject.creationDateServerTimeStamp = project.creationDateServerTimeStamp;
    this.currentProject.modificationDateServerTimeStamp = project.modificationDateServerTimeStamp;
    this.currentProject.modificationDate = project.modificationDate;
    this.currentProject.currentVersion = project.currentVersion;
    this.currentProject.currentVersionDisplayString = project.currentVersionDisplayString;
    this.currentProject.finalizationState = project.finalizationState;
    this.currentProject.articleNumber = project.articleNumber;
    this.currentProject.articleNumberLong = project.articleNumberLong;
    this.currentProject.drawingNumber = project.drawingNumber;
    this.currentProject.fNumber = project.fNumber;
    this.currentProject.originProjectArticleNumberLong = project.originProjectArticleNumberLong;
    this.currentProject.typeClassId = project.typeClassId;
    this.currentProject.documentVersion = project.documentVersion;
    this.currentProject.isLatestVersion = project.isLatestVersion;
    this.currentProject.name = project.name;
    this.currentProject.representations = project.representations;
    this.currentProject.options = project.options;
    this.currentProject.totalPrice = project.totalPrice;
    this.currentProject.totalDemand = project.totalDemand;
    this.currentProject.batchSize = project.batchSize;
    this.currentProject.isArticleNumberGenerated = project.isArticleNumberGenerated;
    this.currentProject.isSharedReadOnly = project.isSharedReadOnly;
    this.currentProject.isLocked = project.isLocked;
    this.currentProject.latestProjectHistoryEntry = project.latestProjectHistoryEntry;
    this.currentProject.isWSPImportedProject = project.isWSPImportedProject;
    this.currentProject.isArticleProject = project.isArticleProject;
    this.currentProject.materialStatus = project.materialStatus;
    this.currentProject.lastCall = project.lastCall;
  }

  public getCompositePartArticleUsagesById(compositeArticleUsageId: string, useStrippedCompositeInfo: boolean = false): ArticleUsageVO[] {
    if (!compositeArticleUsageId) {
      return [];
    }

    const result: ArticleUsageVO[] = [];
    for (const articleUsage of this.getAllArticleUsages()) {
      const compArticleUsage: string = ArticleUsageUtils.getRelatedCompositeArticleUsageId(articleUsage, useStrippedCompositeInfo);
      if (compArticleUsage === compositeArticleUsageId) {
        result.push(articleUsage);
      }
    }
    return result;
  }

  public setFinalizationInfos(projectHead: ProjectHeadVO) {
    this.currentProject.documentVersion = projectHead.documentVersion;
    this.currentProject.materialStatus = projectHead.materialStatus;
    this.currentProject.lastCall = projectHead.lastCall;
    this.currentProject.fNumber = projectHead.fNumber;
    this.currentProject.articleNumberLong = projectHead.articleNumberLong;
    this.sendProjectChanged(ProjectChangedType.ProjectChange, this.currentProject);
    this.notifyCurrentProjectChangedInternal();
  }
}
